import { Col, Row } from '@wyz/ui';
import { CUSTOMER_LEVEL } from '@wyz/types';
import { useFormContext, useWatch } from 'react-hook-form';
import { SelectField } from '~/common/components';
import { Trans } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import {
  SeasonSelector,
  VehicleTypeGroupToggle,
  WcwAutocomplete,
} from '~/purchase/components';
import { useWcwCatalog } from '~/purchase/api';
import { COMPLETE_WHEEL_VEHICLE_TYPE } from '~/purchase/helpers';
import { useSearchMode } from '~/purchase/hooks';
import { IWcwSearchFormSchema } from '~/purchase/schemas';

// eslint-disable-next-line @wyz/module/no-relative-import
import './search-bar.scss';
import { useManageParameters } from '~/common/api';

type WcwSearchBarProps = {
  customer: IUserCustomer;
};

function WcwSearchBar({ customer }: WcwSearchBarProps) {
  const methods = useFormContext<IWcwSearchFormSchema>();
  const [seasons, brand, model, rawIdentifier, identifier] = useWatch({
    control: methods.control,
    name: ['seasons', 'brand', 'model', 'rawIdentifier', 'identifier'],
  });

  const { isQuickSearch } = useSearchMode({
    rawIdentifier,
    identifierValue: identifier?.value,
    onSwitchToManual: () => {
      methods.setValue('identifier', null);
    },
  });

  const manageParameters = useManageParameters({
    getSeasons: {
      enabled: true,
    },
  });

  const wcwCatalog = useWcwCatalog({
    getBrands: {
      enabled: !isQuickSearch,
      args: { customerId: customer.id },
    },
    getModels: {
      enabled: !isQuickSearch && !!brand,
      args: { customerId: customer.id, brand: brand ?? '' },
    },
    getVersions: {
      enabled: !isQuickSearch && !!brand && !!model,
      args: { customerId: customer.id, brand: brand ?? '', model: model ?? '' },
    },
    getSupplierAvailability: {
      enabled: !isQuickSearch,
      args: { customerId: customer.id },
    },
  });

  const wcwAvailableSeasonKeys = new Set(
    wcwCatalog.getSupplierAvailability.data?.data
      .flatMap((s) => s.availableSeasons)
      .map((s) => s.publicKey) ?? [],
  );

  const toOptions = (items: Array<{ publicKey: string; name: string }>) =>
    items.map((item) => ({ label: item.name, value: item.publicKey }));

  return (
    <>
      <Row>
        <Col lg={12} md={12}>
          <VehicleTypeGroupToggle
            groups={(customer.settingTemplate?.vehicleTypeGroups ?? []).filter(
              (g) =>
                customer.level < CUSTOMER_LEVEL.N2 ||
                g.name !== COMPLETE_WHEEL_VEHICLE_TYPE,
            )}
            activeGroup='WCW'
            onSelectGroup={() => {}}
          />
        </Col>
        <Col lg={6} md={12}>
          <WcwAutocomplete size='sm' />
        </Col>
      </Row>
      {!isQuickSearch && (
        <Row>
          <Col lg={3} md={6}>
            <SelectField
              name='brand'
              label={<Trans i18nKey='app.purchase.wcw.search_page.brand' />}
              options={toOptions(wcwCatalog.getBrands.data?.data ?? [])}
              isLoading={wcwCatalog.getBrands.isProcessing}
              isClearable
              size='sm'
              onSelect={() => {
                methods.setValue('model', null);
                methods.setValue('versions', null);
              }}
            />
          </Col>
          <Col lg={3} md={6}>
            <SelectField
              name='model'
              label={<Trans i18nKey='app.purchase.wcw.search_page.model' />}
              options={toOptions(wcwCatalog.getModels.data?.data ?? [])}
              isLoading={wcwCatalog.getModels.isProcessing}
              isDisabled={!brand}
              isClearable
              size='sm'
              onSelect={() => {
                methods.setValue('versions', null);
              }}
            />
          </Col>
          <Col lg={3} md={6}>
            <SelectField<true>
              isMulti
              name='versions'
              label={<Trans i18nKey='app.purchase.wcw.search_page.versions' />}
              options={toOptions(wcwCatalog.getVersions.data?.data ?? [])}
              isLoading={wcwCatalog.getVersions.isProcessing}
              isDisabled={!brand || !model}
              isClearable
              size='sm'
            />
          </Col>
          <Col lg={3} md={6}>
            <SeasonSelector
              seasons={seasons ?? []}
              availableSeasons={manageParameters.getSeasons.data?.data ?? []}
              isLoading={
                manageParameters.getSeasons.isProcessing ||
                wcwCatalog.getSupplierAvailability.isProcessing
              }
              disabledSeasons={manageParameters.getSeasons.data?.data
                .filter((s) => !wcwAvailableSeasonKeys.has(s.publicKey))
                .map((s) => s.publicKey)}
              onChange={(newSeasons) => {
                methods.setValue('seasons', newSeasons, {
                  shouldValidate: true,
                });
              }}
              errorMessage={methods.formState.errors.seasons?.message}
              required
            />
          </Col>
        </Row>
      )}
    </>
  );
}

export default WcwSearchBar;
